import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink } from '../../components/site/Pill';
import { SchemeGrid } from '../../components/site/SchemeGrid';
import { INTERIORS } from '../../lib/site';

/**
 * The homepage, section for section as lotusliving.co.uk — with one change the brief
 * asked for: where the site sends people to an enquiry form, we show what is actually
 * available, live from the platform.
 */
export default function SiteHome() {
  return (
    <SiteLayout overlay>
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative h-[100svh] min-h-[600px] w-full overflow-hidden bg-ink-900">
        <img src={INTERIORS.hero} alt="" className="absolute inset-0 h-full w-full animate-fade-in object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/55 via-ink-900/15 to-ink-900/55" />
        <div className="relative z-10 flex h-full items-center justify-center px-6 text-center">
          <h1 className="display max-w-[900px] animate-fade-up text-[46px] leading-[1.02] text-white sm:text-[68px] lg:text-[92px]" style={{ animationDelay: '150ms' }}>
            Homes Designed for Modern Renters
          </h1>
        </div>
        <a href="#belong" className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 text-white/80 transition hover:text-white" aria-label="Scroll">
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </a>
      </section>

      {/* ── Two-column text ──────────────────────────────────────────────────── */}
      <section id="belong" className="site-narrow grid gap-10 py-24 md:grid-cols-2 md:gap-20 lg:py-36">
        <h2 className="display text-[36px] text-ink-900 sm:text-[44px] lg:text-[54px]">More Than a Place to Live. Our Properties Offer A Place to Belong</h2>
        <div className="md:pt-3">
          <p className="max-w-[674px] text-[17px] leading-[1.7] text-ink-600">
            At Lotus Living, we believe renting should never mean compromising on quality, comfort, or experience.
            That's why our spacious, stylish, and excellently located homes are purpose-built for renters from the
            ground up. Every property is thoughtfully designed, expertly constructed, and professionally managed to
            deliver a better way to live.
          </p>
          <PillLink to="/rent" className="mt-10 lg:mt-12">Find your new home</PillLink>
        </div>
      </section>

      {/* ── Floating gallery ─────────────────────────────────────────────────── */}
      <section className="site-container pb-24 lg:pb-40">
        <div className="grid grid-cols-6 gap-3 sm:gap-5 lg:gap-7">
          <Photo src={INTERIORS.bedroom} className="col-span-3 aspect-[3/4] sm:col-span-2 lg:mt-16" />
          <Photo src={INTERIORS.harlow} className="col-span-3 aspect-[4/3] sm:col-span-4 sm:aspect-[16/9]" />
          <Photo src={INTERIORS.kitchen} className="col-span-6 aspect-[16/9] sm:col-span-3 sm:aspect-[4/3]" />
          <Photo src={INTERIORS.kitchenDetail} className="col-span-3 aspect-[4/3] sm:col-span-2 sm:aspect-[3/4]" />
          <Photo src={INTERIORS.bathroom} className="col-span-3 aspect-[4/3] sm:col-span-1 sm:aspect-[3/4] lg:mt-12" />
        </div>
      </section>

      {/* ── Simply great rental homes ────────────────────────────────────────── */}
      <section className="bg-cream">
        <div className="site-narrow grid items-center gap-14 py-24 md:grid-cols-2 lg:gap-24 lg:py-36">
          <div className="relative">
            <img src={INTERIORS.livingArea} alt="Bright open-plan kitchen, dining and living area at Drake House" className="aspect-[4/3] w-full rounded-[10px] object-cover" loading="lazy" />
            <img src={INTERIORS.welcomeHamper} alt="Welcome hamper at Drake House" className="absolute -bottom-10 -right-4 hidden w-[42%] rounded-[10px] border-[6px] border-cream object-cover shadow-float sm:block lg:-right-12" loading="lazy" />
          </div>
          <div>
            <p className="eyebrow">Built for renters</p>
            <h2 className="display mt-4 text-[40px] text-ink-900 sm:text-[52px] lg:text-[64px]">Simply Great <em>Rental Homes</em></h2>
            <div className="mt-8 space-y-6 text-[16px] leading-[1.75] text-ink-600">
              <p>Every Lotus Living property is carefully crafted to support real people and modern lifestyles. Expect generous living spaces filled with natural light, large kitchens equipped with premium appliances, and elegant finishes like quality wood flooring and sleek, durable surfaces.</p>
              <p>We believe that great homes should be in great locations, too. That's why our developments are placed close to transport links, green spaces, shops, and everyday essentials, with everything you need to enjoy life right on your doorstep.</p>
            </div>
            <Link to="/about" className="copy-link mt-8 inline-block text-[15px] font-medium">About Us</Link>
          </div>
        </div>
      </section>

      {/* ── Explore properties (live availability, not an enquiry form) ──────── */}
      <section className="site-container py-24 lg:py-36">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Live availability</p>
            <h2 className="display mt-4 text-[44px] text-ink-900 sm:text-[56px] lg:text-[72px]"><em>Explore</em> Properties</h2>
          </div>
          <PillLink to="/rent" variant="ghost">View all</PillLink>
        </div>
        <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-ink-500">Prices and availability below are live from our lettings platform. Pick a scheme to see the exact homes ready to move into.</p>
        <SchemeGrid className="mt-12" />
      </section>

      {/* ── Why Lotus ────────────────────────────────────────────────────────── */}
      <section className="border-t border-ink-100">
        <div className="site-narrow grid gap-10 py-24 md:grid-cols-2 md:gap-20 lg:py-36">
          <h2 className="display text-[44px] text-ink-900 sm:text-[56px] lg:text-[72px]"><em>Why</em> Lotus?</h2>
          <div className="space-y-6 text-[16px] leading-[1.75] text-ink-600 md:pt-4">
            <p>At Lotus Living, we're reimagining what renting can be. Our homes aren't adapted for renters; they're made for them. That means better layouts, smarter amenities, and a genuine commitment to long-term comfort.</p>
            <p>From high-spec finishes and energy-efficient design to responsive on-site management and modern amenities, every detail is chosen with care and attention, and with renters in mind.</p>
            <p>We're not just building homes, we're building trust, peace of mind, convenience, satisfaction, great lifestyles, and even better ways of living for everyone.</p>
            <PillLink to="/living-with-us" className="!mt-10">Living With Us</PillLink>
          </div>
        </div>
      </section>

      {/* ── Banner ───────────────────────────────────────────────────────────── */}
      <section className="relative flex min-h-[70vh] items-center justify-center overflow-hidden bg-ink-900">
        <img src={INTERIORS.kitchen} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-ink-900/55" />
        <div className="relative z-10 px-6 py-28 text-center">
          <h2 className="display mx-auto max-w-[820px] text-[44px] text-white sm:text-[60px] lg:text-[76px]">Start Living the Way You Deserve</h2>
          <PillLink to="/rent" variant="white" className="mt-10 lg:mt-14">Find your new home</PillLink>
        </div>
      </section>
    </SiteLayout>
  );
}

function Photo({ src, className = '' }: { src: string; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-[10px] bg-ink-100 ${className}`}>
      <img src={src} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.03]" />
    </div>
  );
}
